import { timestampToMoment } from '@weenat/client/dist/core/history'
import { FontSizeToken, WeenatColor } from '@weenat/theme'
import { useIntl } from '@weenat/wintl'
import { isNaN, isNil } from 'lodash-es'
import Card from './Card'
import DelimitedFlex from './DelimitedFlex'
import Icons from './Icons'
import Text from './Text'
import TextTooltip from './tooltips/TextTooltip'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltips/Tooltip'

const Icon = Icons.Schedule

const defaultColor = 'grayscale.700' as const

const contentStyle = { backgroundColor: 'transparent', boxShadow: 'none' }

export interface LastUpdatePrimitiveProps {
  /**
   * Date of the update in a unix timestamp format
   */
  timestamp: number | null | undefined
  /**
   * Timezone of the ressource. Used to display the exact date in Tooltips
   */
  timezone: string | undefined
  /**
   * `style` parameter of `Intl.RelativeTimeFormatOptions`
   * @default 'short'
   */
  format?: 'long' | 'narrow' | 'short'
  /**
   * Color of the text and icon.
   */
  color?: WeenatColor
  /**
   * Font size of the label
   * @default 'sm'
   */
  fontSize?: FontSizeToken
  /**
   * Size of the icon
   * @default 'rg'
   */
  iconSize?: FontSizeToken
  /**
   * Whether or not to disable the date tooltip
   * @default 'false'
   */
  disableTooltip?: boolean
  isVWSBadge?: boolean
}

/**
 * Dumb component that will display the given timestamp in an rtf compared to now in the given format and color
 */
const LastUpdatePrimitive: FC<LastUpdatePrimitiveProps> = ({
  timestamp,
  format,
  color = defaultColor,
  timezone,
  fontSize = 'sm',
  iconSize = 'rg',
  disableTooltip = false,
  isVWSBadge = false
}) => {
  const { t } = useIntl()
  const { formatRelativeTimeToNow } = useIntl()

  const timeAgo =
    isNil(timestamp) || !isFinite(timestamp)
      ? '−'
      : formatRelativeTimeToNow(timestamp, { style: format })

  const trigger = (
    <Flex $alignItems='center' $justifyContent='center' $gap={'sm'}>
      <Icon $color={color} $size={iconSize} />
      <Text $lineHeight={iconSize} $fontSize={fontSize} $color={color} $userSelect='none'>
        {timeAgo}
      </Text>
    </Flex>
  )

  if (isNil(timestamp) || isNaN(timestamp)) {
    return trigger
  }

  if (!isVWSBadge) {
    return (
      <TextTooltip
        disabled={disableTooltip}
        content={timestampToMoment(timestamp, timezone).format('D MMMM YYYY, HH:mm')}
      >
        {trigger}
      </TextTooltip>
    )
  }

  const content = (
    <Card $p={0} $maxWidth={320} $boxShadow='md'>
      <DelimitedFlex $flexDirection='column' $p='md' $gap='md'>
        <Text $fontWeight='bold'>{t('summaries.virtual_weather_station_delay_help.title')}</Text>
        <Text>{t('summaries.virtual_weather_station_delay_help.description')}</Text>
      </DelimitedFlex>
      <Flex $p='md'>
        <Text>{timestampToMoment(timestamp, timezone).format('D MMMM YYYY, HH:mm')}</Text>
      </Flex>
    </Card>
  )

  return (
    <Tooltip disabled={disableTooltip}>
      <TooltipTrigger>{trigger}</TooltipTrigger>
      <TooltipContent style={contentStyle}>{content}</TooltipContent>
    </Tooltip>
  )
}

export default LastUpdatePrimitive
